import {getLogger} from './logging'
import {setupErrorTracking} from './errorAnalytics'
import {
  detectAndTrackBookNow,
  analyzeFirstTimeUserJourney
} from './funnelAnalytics'
import {
  STANDARD_EVENTS,
  StandardPageViewEvent,
  StandardNavigationEvent,
  StandardClickEvent,
  StandardUserJourneyEvent,
  StandardExitIntentEvent
} from './analyticsInterfaces'
import {
  getCurrentPageInfo,
  createBaseEventData,
  getOrCreateVisitorData,
  pushDataLayerEvent,
  startPageSessionTracking,
  endPageSessionTracking,
  initializeGA4,
  getVisitorType,
  getCurrentUser,
  setStorageItem,
  getCachedStorageData,
  refreshPageInfoCache,
  refreshStorageCache,
  debounce,
  USER_JOURNEY_STORAGE_KEY,
  PREVIOUS_PAGE_STORAGE_KEY
} from './analyticsHelpers'

const logger = getLogger('coreAnalytics')

/**
 * Tracks a page view with visitor information
 */
export function trackPageView(userId?: string, pagePath?: string): void {
  try {
    const baseData = createBaseEventData(userId)
    const visitorData = getOrCreateVisitorData()
    const visitorType = getVisitorType(visitorData)
    const normalizedVisitorType: 'new' | 'returning' =
      visitorType === 'unique' ? 'new' : (visitorType as 'new' | 'returning')

    const event: StandardPageViewEvent = {
      event: STANDARD_EVENTS.PAGE_VIEW,
      ...baseData,
      visitor_type: normalizedVisitorType,
      // Override page info if provided
      ...(pagePath && {page_path: pagePath})
    }

    logger.debug({
      message: 'Tracking page view',
      data: {
        event,
        visitorData: {
          visitorType,
          visitCount: visitorData.visitCount,
          sessionId: visitorData.sessionId
        }
      }
    })

    pushDataLayerEvent(event)

    // Directly add to user journey - every route change gets tracked
    const storageData = getCachedStorageData()

    let journeyData = {
      sessionId: visitorData.sessionId,
      pages: [] as string[],
      startTime: Date.now()
    }

    if (storageData.userJourney) {
      journeyData = storageData.userJourney
    }

    journeyData.pages.push(event.page_path)

    setStorageItem(USER_JOURNEY_STORAGE_KEY, journeyData)
  } catch (error) {
    logger.error({
      message: 'Error tracking page view',
      data: {error}
    })
  }
}

/**
 * Tracks navigation from one page to another
 */
export function trackNavigation(
  fromPage: string,
  toPage: string,
  navigationType:
    | 'link_click'
    | 'back_button'
    | 'forward_button'
    | 'browser_navigation'
    | 'programmatic' = 'link_click',
  userId?: string
): void {
  try {
    const baseData = createBaseEventData(userId)

    const navigationEvent: StandardNavigationEvent = {
      event: STANDARD_EVENTS.NAVIGATION,
      ...baseData,
      from_page: fromPage,
      to_page: toPage,
      navigation_type: navigationType
    }

    pushDataLayerEvent(navigationEvent)
    updateUserJourney(toPage)
  } catch (error) {
    logger.error({
      message: 'Error tracking navigation',
      data: {error}
    })
  }
}

/**
 * Tracks clicks on specific elements
 */
export function trackClick(
  element: HTMLElement | string,
  destinationUrl?: string,
  userId?: string
): void {
  try {
    const baseData = createBaseEventData(userId)

    let clickElement: string
    let elementType: string
    let clickContext: string | undefined

    if (typeof element === 'string') {
      clickElement = element
      elementType = 'text'
      clickContext = undefined
    } else {
      clickElement = element.tagName.toLowerCase()
      elementType = element.tagName.toLowerCase()
      clickContext = element.className || undefined
    }

    const clickEvent: StandardClickEvent = {
      event: STANDARD_EVENTS.CLICK,
      ...baseData,
      click_element: clickElement,
      element_type: elementType,
      destination_url: destinationUrl,
      click_context: clickContext
    }

    pushDataLayerEvent(clickEvent)
  } catch (error) {
    logger.error({
      message: 'Error tracking click',
      data: {error}
    })
  }
}

/**
 * Updates the user journey tracking
 */
function updateUserJourney(currentPage: string): void {
  try {
    const visitorData = getOrCreateVisitorData()
    const storageData = getCachedStorageData()

    let journeyData = {
      sessionId: visitorData.sessionId,
      pages: [] as string[],
      startTime: Date.now()
    }

    if (storageData.userJourney) {
      journeyData = storageData.userJourney
    }

    // Add current page to journey - always add unless it's exactly the same page within 1 second
    const shouldAddPage =
      journeyData.pages.length === 0 ||
      journeyData.pages[journeyData.pages.length - 1] !== currentPage

    if (shouldAddPage) {
      journeyData.pages.push(currentPage)
    }

    setStorageItem(USER_JOURNEY_STORAGE_KEY, journeyData)
  } catch (error) {
    logger.error({
      message: 'Error updating user journey',
      data: {error}
    })
  }
}

/**
 * Sends the complete user journey event
 */
export function sendUserJourneyEvent(userId?: string): void {
  try {
    const storageData = getCachedStorageData()
    if (!storageData.userJourney) {
      return
    }

    const journeyData = storageData.userJourney

    const sessionDuration = (Date.now() - journeyData.startTime) / 1000 / 60 // minutes

    const baseData = createBaseEventData(userId)

    const userJourneyEvent: StandardUserJourneyEvent = {
      event: STANDARD_EVENTS.USER_JOURNEY,
      ...baseData,
      journey_path: journeyData.pages,
      total_pages: journeyData.pages.length,
      session_duration_minutes: Math.round(sessionDuration * 100) / 100
    }

    pushDataLayerEvent(userJourneyEvent)

    // Clear the journey data after sending
    sessionStorage.removeItem(USER_JOURNEY_STORAGE_KEY)
  } catch (error) {
    logger.error({
      message: 'Error sending user journey event',
      data: {error}
    })
  }
}

/**
 * Debug function to check current user journey status
 */
export function getUserJourneyStatus(): any {
  try {
    const storageData = getCachedStorageData()
    if (!storageData.userJourney) {
      return null
    }

    const journeyData = storageData.userJourney
    const status = {
      sessionId: journeyData.sessionId,
      pages: journeyData.pages,
      totalPages: journeyData.pages.length,
      startTime: new Date(journeyData.startTime).toLocaleString(),
      currentUser: getCurrentUser(),
      journeyPath: journeyData.pages.join(' → '),
      currentLocation: getCurrentPageInfo().pathname
    }

    return status
  } catch (error) {
    console.error('Error getting journey status:', error)
    return null
  }
}

/**
 * Force add current page to journey (for testing)
 */
export function forceAddCurrentPageToJourney(): void {
  try {
    const currentPage = getCurrentPageInfo().pathname

    // Force update the journey with current page
    const visitorData = getOrCreateVisitorData()
    const storageData = getCachedStorageData()

    let journeyData = {
      sessionId: visitorData.sessionId,
      pages: [] as string[],
      startTime: Date.now()
    }

    if (storageData.userJourney) {
      journeyData = storageData.userJourney
    }

    // Force add current page regardless of duplicates
    journeyData.pages.push(currentPage)

    setStorageItem(USER_JOURNEY_STORAGE_KEY, journeyData)

    getUserJourneyStatus()
  } catch (error) {
    console.error('Error forcing page add:', error)
  }
}

/**
 * Tracks exit intent when user is about to leave
 */
export function trackExitIntent(
  exitType: 'tab_close' | 'navigate_away' | 'back_button' | 'page_refresh',
  userId?: string
): void {
  try {
    const storageData = getCachedStorageData()
    if (!storageData.pageSession) {
      return
    }

    const sessionData = storageData.pageSession
    const timeOnPage = Math.round((Date.now() - sessionData.startTime) / 1000)

    const baseData = createBaseEventData(userId)

    const exitIntentEvent: StandardExitIntentEvent = {
      event: STANDARD_EVENTS.EXIT_INTENT,
      ...baseData,
      exit_page: baseData.page_path,
      time_on_page_seconds: timeOnPage,
      exit_type: exitType
    }

    pushDataLayerEvent(exitIntentEvent)
  } catch (error) {
    logger.error({
      message: 'Error tracking exit intent',
      data: {error}
    })
  }
}

// Store event listeners for cleanup
interface AnalyticsEventListeners {
  clickHandler: (event: Event) => void
  beforeUnloadHandler: (event: BeforeUnloadEvent) => void
  visibilityChangeHandler: (event: Event) => void
  popStateHandler: (event: PopStateEvent) => void
}

let analyticsListeners: AnalyticsEventListeners | null = null

/**
 * Sets up automatic click tracking for common elements
 */
function setupAutomaticClickTracking(): void {
  try {
    // Create click handler function
    const clickHandler = (event: Event) => {
      const target = event.target as HTMLElement

      // Check if it's a button, link, or has specific data attributes
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.getAttribute('data-track-click') === 'true' ||
        target.closest('[data-track-click="true"]')
      ) {
        const trackingTarget =
          target.closest('[data-track-click="true"]') || target
        const destinationUrl =
          (trackingTarget as HTMLAnchorElement).href || undefined

        trackClick(trackingTarget as HTMLElement, destinationUrl)

        // Check if this is a "Book Now" click
        checkBookNowClick(trackingTarget as HTMLElement, destinationUrl)
      }
    }

    // Store reference for cleanup
    if (!analyticsListeners) {
      analyticsListeners = {} as AnalyticsEventListeners
    }
    analyticsListeners.clickHandler = clickHandler

    // Add event listener
    document.addEventListener('click', clickHandler)
  } catch (error) {
    logger.error({
      message: 'Error setting up automatic click tracking',
      data: {error}
    })
  }
}

/**
 * Checks if the clicked element is a "Book Now" action
 */
function checkBookNowClick(
  element: HTMLElement,
  destinationUrl?: string
): void {
  try {
    // Use the new improved book now detection
    detectAndTrackBookNow(element, destinationUrl)
  } catch (error) {
    logger.error({
      message: 'Error checking book now click',
      data: {error}
    })
  }
}

/**
 * Detects navigation type based on browser behavior
 */
function detectNavigationType():
  | 'link_click'
  | 'back_button'
  | 'forward_button'
  | 'browser_navigation'
  | 'programmatic' {
  // This is a simplified detection - in practice, this is quite complex
  // For now, we'll default to 'link_click' and enhance later if needed
  if (performance.navigation.type === 1) {
    return 'browser_navigation' // page refresh
  } else if (performance.navigation.type === 2) {
    return 'back_button'
  } else {
    return 'link_click'
  }
}

/**
 * Initializes basic analytics tracking components
 */
function initializeBasicTracking(): void {
  // Initialize GA4
  initializeGA4()

  // Track initial page view
  trackPageView()

  // Start session tracking for initial page
  startPageSessionTracking()

  // Initialize user journey tracking
  updateUserJourney(getCurrentPageInfo().pathname)

  // Set up automatic click tracking
  setupAutomaticClickTracking()

  // Set up error tracking
  setupErrorTracking()
}

/**
 * Stores initial previous page info for navigation tracking
 */
function initializePreviousPageStorage(): void {
  const pageInfo = getCurrentPageInfo()
  setStorageItem(PREVIOUS_PAGE_STORAGE_KEY, {
    path: pageInfo.pathname
  })
}

/**
 * Sets up DOM mutation observer for SPA route change detection
 */
function setupRouteChangeObserver(): void {
  // Track page views, session duration, and navigation on route changes (for SPAs)
  let currentPageInfo = getCurrentPageInfo()

  // Debounced route change handler for performance
  const handleRouteChange = debounce(() => {
    const newPageInfo = refreshPageInfoCache() // Force refresh cache on route change

    if (
      newPageInfo.pathname !== currentPageInfo.pathname ||
      newPageInfo.title !== currentPageInfo.title
    ) {
      const storageData = refreshStorageCache() // Force refresh on route change
      const previousPage = storageData.previousPage

      // Track navigation if we have previous page info
      if (previousPage) {
        trackNavigation(
          previousPage.path,
          newPageInfo.pathname,
          detectNavigationType()
        )
      }

      // End tracking for previous page
      endPageSessionTracking()

      // Update current page info
      currentPageInfo = newPageInfo

      // Track new page view
      trackPageView()

      // Start tracking for new page
      startPageSessionTracking()

      // Update user journey
      updateUserJourney(currentPageInfo.pathname)

      // Store new previous page info
      setStorageItem(PREVIOUS_PAGE_STORAGE_KEY, {
        path: currentPageInfo.pathname,
        title: currentPageInfo.title
      })
    }
  }, 50) // 50ms debounce to avoid excessive calls

  // Optimized mutation observer - only watch for title changes and minimal DOM changes
  const observer = new MutationObserver(handleRouteChange)

  // More targeted observation - only title changes and head modifications
  observer.observe(document.head, {
    childList: true,
    subtree: true,
    characterData: true
  })

  // Also observe title element specifically if it exists
  const titleElement = document.querySelector('title')
  if (titleElement) {
    observer.observe(titleElement, {
      childList: true,
      characterData: true
    })
  }
}

/**
 * Sets up window event listeners for exit tracking and navigation
 */
function setupWindowEventListeners(): void {
  // Create beforeunload handler
  const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    // Try to get current user for journey tracking
    const currentUser = getCurrentUser()

    trackExitIntent('tab_close', currentUser)
    endPageSessionTracking()
    sendUserJourneyEvent(currentUser)
    analyzeFirstTimeUserJourney('page_unload')
  }

  // Create visibility change handler
  const visibilityChangeHandler = () => {
    if (document.hidden) {
      // Try to get current user for journey tracking
      const currentUser = getCurrentUser()

      trackExitIntent('navigate_away', currentUser)
      endPageSessionTracking()

      // ALWAYS send user journey event on session end (regardless of user type)
      sendUserJourneyEvent(currentUser)

      analyzeFirstTimeUserJourney('tab_switch') // Key fix: distinguish tab switch from page unload
    } else {
      // Restart tracking when page becomes visible again
      startPageSessionTracking()
    }
  }

  // Create popstate handler
  const popStateHandler = (event: PopStateEvent) => {
    const storageData = refreshStorageCache() // Force refresh on navigation
    const previousPage = storageData.previousPage

    if (previousPage) {
      const pageInfo = refreshPageInfoCache() // Refresh cache on navigation
      trackNavigation(previousPage.path, pageInfo.pathname, 'back_button')
    }
  }

  // Store references for cleanup
  if (!analyticsListeners) {
    analyticsListeners = {} as AnalyticsEventListeners
  }
  analyticsListeners.beforeUnloadHandler = beforeUnloadHandler
  analyticsListeners.visibilityChangeHandler = visibilityChangeHandler
  analyticsListeners.popStateHandler = popStateHandler

  // Track exit intent and send journey when user leaves
  window.addEventListener('beforeunload', beforeUnloadHandler)

  // Track page visibility changes
  document.addEventListener('visibilitychange', visibilityChangeHandler)

  // Track browser back/forward buttons
  window.addEventListener('popstate', popStateHandler)
}

/**
 * Initializes core analytics tracking
 * Should be called once when the app starts
 */
export function initializeCoreAnalytics(): void {
  try {
    // 1. Initialize basic tracking components
    initializeBasicTracking()

    // 2. Set up previous page storage
    initializePreviousPageStorage()

    // 3. Set up route change observer for SPA navigation
    setupRouteChangeObserver()

    // 4. Set up window event listeners for exit tracking
    setupWindowEventListeners()

    logger.info({
      message:
        'Core analytics initialized successfully with navigation flow tracking, device tracking, and error tracking'
    })
  } catch (error) {
    logger.error({
      message: 'Error initializing core analytics',
      data: {error}
    })
  }
}

/**
 * Cleans up analytics event listeners to prevent memory leaks
 * Should be called when the component/app unmounts
 */
export function cleanupCoreAnalytics(): void {
  try {
    if (analyticsListeners) {
      // Remove all event listeners
      if (analyticsListeners.clickHandler) {
        document.removeEventListener('click', analyticsListeners.clickHandler)
      }

      if (analyticsListeners.beforeUnloadHandler) {
        window.removeEventListener(
          'beforeunload',
          analyticsListeners.beforeUnloadHandler
        )
      }

      if (analyticsListeners.visibilityChangeHandler) {
        document.removeEventListener(
          'visibilitychange',
          analyticsListeners.visibilityChangeHandler
        )
      }

      if (analyticsListeners.popStateHandler) {
        window.removeEventListener(
          'popstate',
          analyticsListeners.popStateHandler
        )
      }

      // Clear the references
      analyticsListeners = null
    }
  } catch (error) {
    logger.error({
      message: 'Error cleaning up analytics event listeners',
      data: {error}
    })
  }
}
